/**
 * Auto-launch follow-up event tracker (#155 Item 3).
 *
 * TSAI's widget emitted two telemetry events that #154 (#116) did
 * not yet ship from the Engage widget:
 *
 *   - `dismissed`     fired when the visitor manually closed the
 *                     chat panel that auto-launched. (Manual closes
 *                     of a FAB-opened panel do NOT count.)
 *   - `user_replied`  fired the first time the visitor sent a
 *                     message after the panel auto-launched. (User
 *                     messages sent after a FAB-open do NOT count.)
 *
 * Both events feed the funnel attribution dashboard so operators
 * can answer: of N auto-launches, how many were dismissed vs.
 * actually engaged with?
 *
 * This module is intentionally a tiny pure-fn state machine so the
 * decision logic can be unit-tested without a DOM. The caller
 * (`EngageChatInterface`) owns the actual `useRef` storage and the
 * emit side effect (POST to `/api/engage/telemetry/autolaunch`).
 *
 * Invariants:
 *   1. At most ONE of `dismissed` / `user_replied` ever fires per
 *      auto-launch cycle. Whichever happens first wins; subsequent
 *      events for the same cycle are no-ops.
 *   2. A second auto-launch in the same visitor session is its own
 *      cycle — `onLaunched` resets state so a new event can fire.
 *   3. A FAB-initiated open does not produce state; calls to
 *      `onPanelClose` / `onFirstUserMessage` while state is null
 *      are no-ops.
 */

/**
 * Opaque tracker state. `null` means "no auto-launch is currently
 * awaiting a follow-up event". `{ configId }` means "the panel
 * auto-launched and we're waiting for the visitor to either
 * dismiss or reply".
 */
export type FollowupState = { readonly configId: string | null } | null;

export type FollowupEventType = 'dismissed' | 'user_replied';

export interface FollowupDecision {
  /** True iff the caller should POST a telemetry event. */
  readonly emit: boolean;
  /** Event type to emit (undefined when `emit` is false). */
  readonly type?: FollowupEventType;
  /** Config id to attach to the event (may be null if unknown). */
  readonly configId?: string | null;
  /** State the caller should write back into its ref. */
  readonly nextState: FollowupState;
}

const NOOP_DECISION: FollowupDecision = { emit: false, nextState: null };

/**
 * Called by the caller when `useAutoLaunch` reports an auto-launch
 * just fired. Resets any prior cycle's state so a re-launch in the
 * same session produces fresh follow-up events.
 */
export function onLaunched(configId: string | null): FollowupState {
  return { configId };
}

/**
 * Called by the caller when the visitor manually closes the chat
 * panel. Emits `dismissed` iff the close happened while a cycle
 * was open.
 */
export function onPanelClose(state: FollowupState): FollowupDecision {
  if (!state) return NOOP_DECISION;
  return {
    emit: true,
    type: 'dismissed',
    configId: state.configId,
    nextState: null,
  };
}

/**
 * Called by the caller before sending the visitor's first message
 * in a cycle. Emits `user_replied` iff the message is the first
 * one of an active auto-launch cycle.
 *
 * Caller is responsible for ensuring this is only invoked for
 * messages they actually intend to send (i.e. not for empty input
 * submits that the form rejects). The state machine itself does
 * not distinguish "first" vs "Nth" — clearing happens on every
 * call when state is set, so subsequent calls in the same cycle
 * naturally no-op.
 */
export function onFirstUserMessage(state: FollowupState): FollowupDecision {
  if (!state) return NOOP_DECISION;
  return {
    emit: true,
    type: 'user_replied',
    configId: state.configId,
    nextState: null,
  };
}
